// Services do painel admin de Processors (superAdmin) — molde
// services/admin/reprocess.ts: queryOptions exportadas p/ teste,
// gcTime 0 na lista (nunca exibir cache velho de configuração),
// metadata com staleTime Infinity (catálogo estático por sessão),
// invalidação por predicate e toasts com getErrorMessage.
import { api } from "@/api"
import { BASE_URL, ENDPOINTS } from "@/constants"
import { toast } from "@/hooks/use-toast"
import i18n from "@/i18n"
import {
  getApiErrorMessage,
  hasStatusCode,
} from "@/lib/api-error"
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@/libs"
import type { MerchantMtlsCertificate } from "@/types/admin-merchant-mtls-certificate"
import type {
  CertificateHistoryResponse,
  ConfigurationEventsResponse,
  ProcessorConfigCreatePayload,
  ProcessorConfigDetail,
  ProcessorConfigItem,
  ProcessorConfigListFilters,
  ProcessorConfigListResponse,
  ProcessorConfigToggleResponse,
  ProcessorConfigUpdatePayload,
  ProcessorGatewayMetadata,
  ProcessorPaymentMethod,
  ProcessorTestConnectionResponse,
} from "@/types/admin-processor-config"

// Chave base das queries da feature — usada também na invalidação.
export const ADMIN_PROCESSOR_CONFIG_QUERY_KEY =
  ENDPOINTS.ADMIN_PROCESSOR_CONFIG

// Monta a query string dos filtros no wire (snake_case, §3).
const buildListQueryString = (
  filters: ProcessorConfigListFilters
): string => {
  const params = new URLSearchParams()
  if (filters.whiteLabelId)
    params.set("white_label_id", filters.whiteLabelId)
  if (filters.gateway)
    params.set("gateway", filters.gateway)
  if (filters.method) params.set("method", filters.method)
  if (filters.active !== undefined)
    params.set("active", String(filters.active))
  if (filters.certificateStatus)
    params.set(
      "certificate_status",
      filters.certificateStatus
    )
  const queryString = params.toString()
  return queryString ? `?${queryString}` : ""
}

// Lista as configurações de processor (GET /admin/processor-config).
export const getProcessorConfigs = async (
  filters: ProcessorConfigListFilters = {}
): Promise<ProcessorConfigListResponse> => {
  const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG}${buildListQueryString(filters)}`
  const { data } =
    await api.get<ProcessorConfigListResponse>(url)
  return data
}

// Metadata por gateway (GET /metadata) — o painel usa `label` e
// `supportsCertificate`; o form dinâmico (wave 2) usa `fields`.
export const getProcessorConfigMetadata = async (): Promise<
  ProcessorGatewayMetadata[]
> => {
  const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_METADATA}`
  const { data } =
    await api.get<ProcessorGatewayMetadata[]>(url)
  // Gateway (heimdall) converte lista vazia em 204 sem corpo, e o axios
  // entrega esse corpo como string vazia — `Array.isArray` é o que protege.
  return Array.isArray(data) ? data : []
}

// Opções da query da lista (exportadas p/ teste).
// - `gcTime: 0` + `refetchOnMount: "always"`: configuração de gateway
//   NUNCA pode renderizar de cache velho ao reentrar na página (mesmo
//   racional do reprocess — skeleton em toda entrada).
// - `placeholderData: undefined` anula o keepPreviousData global.
export const processorConfigListQueryOptions = (
  filters: ProcessorConfigListFilters = {}
) => ({
  queryKey: [
    ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
    "list",
    filters,
  ] as const,
  queryFn: async () => getProcessorConfigs(filters),
  refetchOnMount: "always" as const,
  gcTime: 0,
  placeholderData: undefined,
})

// Opções da query de metadata — catálogo estático por sessão.
export const processorConfigMetadataQueryOptions = () => ({
  queryKey: [
    ENDPOINTS.ADMIN_PROCESSOR_CONFIG_METADATA,
  ] as const,
  queryFn: getProcessorConfigMetadata,
  staleTime: Infinity,
})

// Hook da lista de configurações.
export const useProcessorConfigs = (
  filters: ProcessorConfigListFilters = {}
) => useQuery(processorConfigListQueryOptions(filters))

// Hook do metadata por gateway.
export const useProcessorConfigMetadata = () =>
  useQuery(processorConfigMetadataQueryOptions())

// Patch imutável do item na lista em cache (update otimista) —
// Spring Page: os itens moram em `content`.
const patchItemActive = (
  cache: ProcessorConfigListResponse | undefined,
  id: string,
  active: boolean
): ProcessorConfigListResponse | undefined => {
  if (!cache) return cache
  return {
    ...cache,
    content: cache.content.map(
      (item): ProcessorConfigItem =>
        item.id === id ? { ...item, active } : item
    ),
  }
}

// O backend sinaliza os conflitos do PADRÃO por TEXTO em `message` (não há
// código de negócio dedicado) — o match é por fragmento estável da frase.
// Sem o mapeamento, o operador leria o inglês cru do payment num toast.
const DEFAULT_CONFLICT_MESSAGE_KEYS: Array<
  [fragment: string, i18nKey: string]
> = [
  [
    "must be active to become the default",
    "defaultRequiresActive",
  ],
  ["another default election", "defaultElectionConflict"],
  ["elect another default", "defaultDeactivationConflict"],
]

// Mensagem acionável (i18n) dos 409 do conceito de padrão; null quando o erro
// não é um deles (o chamador cai no getApiErrorMessage de sempre).
const defaultConflictMessage = (
  error: unknown
): string | null => {
  if (!hasStatusCode(error, 409)) return null
  const message = getApiErrorMessage(error, "")
  const match = DEFAULT_CONFLICT_MESSAGE_KEYS.find(
    ([fragment]) => message.includes(fragment)
  )
  return match
    ? i18n.t(
        `pages.adminProcessorConfig.byMethod.errors.${match[1]}`
      )
    : null
}

// Contexto de rollback do toggle otimista.
type ToggleSnapshotContext = {
  previous: Array<
    [
      readonly unknown[],
      ProcessorConfigListResponse | undefined,
    ]
  >
}

// Fábrica do toggle activate/deactivate: otimista em TODAS as
// queries de lista da feature, rollback no erro, invalidação por
// predicate no settled (consolida o estado real do servidor).
const useToggleActiveMutation = (
  action: "activate" | "deactivate"
) => {
  const queryClient = useQueryClient()
  const endpoint =
    action === "activate"
      ? ENDPOINTS.ADMIN_PROCESSOR_CONFIG_ACTIVATE
      : ENDPOINTS.ADMIN_PROCESSOR_CONFIG_DEACTIVATE

  return useMutation<
    ProcessorConfigToggleResponse,
    unknown,
    string,
    ToggleSnapshotContext
  >({
    mutationFn: async (uuid: string) => {
      const url = `${BASE_URL}${endpoint.replace("{{uuid}}", uuid)}`
      const { data } =
        await api.post<ProcessorConfigToggleResponse>(url)
      return data
    },
    onMutate: async (uuid) => {
      await queryClient.cancelQueries({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
      })
      const previous = queryClient.getQueriesData<
        ProcessorConfigListResponse | undefined
      >({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
      })
      queryClient.setQueriesData<
        ProcessorConfigListResponse | undefined
      >(
        {
          predicate: (query) =>
            query.queryKey[0] ===
            ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
        },
        (cache) =>
          patchItemActive(
            cache,
            uuid,
            action === "activate"
          )
      )
      return { previous }
    },
    onSuccess: (result, uuid) => {
      // O activate AUTO-ELEGE o padrão quando o par está sem (regra V176) e o
      // response devolve `defaultForMethod` — o cache precisa espelhar na
      // hora, senão o badge "Sem padrão" mente até o refetch do settled.
      // Ausente (backend antigo) = não mexe.
      if (result.defaultForMethod !== undefined) {
        queryClient.setQueriesData<
          ProcessorConfigListResponse | undefined
        >(
          {
            predicate: (query) =>
              query.queryKey[0] ===
              ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
          },
          (cache) =>
            patchDefaultForMethodFromToggle(
              cache,
              uuid,
              result.defaultForMethod === true
            )
        )
      }
      // Deactivate da última config ativa do par WL+método → 200 com
      // `warning` (§3) — exibido de forma destacada, nunca silencioso.
      if (result.warning) {
        toast({
          title: result.warning,
          variant: "destructive",
        })
        return
      }
      toast({
        title:
          action === "activate"
            ? "Configuração ativada"
            : "Configuração desativada",
        variant: "success",
      })
    },
    onError: (error, _uuid, context) => {
      // Rollback do otimismo: restaura cada snapshot capturado.
      context?.previous.forEach(([queryKey, cache]) => {
        queryClient.setQueryData(queryKey, cache)
      })
      // Desativar a config PADRÃO havendo outras ativas volta 409 — o toast
      // precisa dizer o caminho ("eleja outro padrão antes"), não só a falha.
      toast({
        title:
          defaultConflictMessage(error) ??
          getApiErrorMessage(
            error,
            action === "activate"
              ? "Erro ao ativar a configuração"
              : "Erro ao desativar a configuração"
          ),
        variant: "destructive",
      })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
      })
    },
  })
}

// Ativa uma configuração (POST /{uuid}/activate).
export const useActivateProcessorConfigMutation = () =>
  useToggleActiveMutation("activate")

// Desativa uma configuração (POST /{uuid}/deactivate).
export const useDeactivateProcessorConfigMutation = () =>
  useToggleActiveMutation("deactivate")

// ── Padrão do par WL+método (V176 — plano abas + default) ──────────

/**
 * Variáveis da eleição de padrão: além do alvo, o PAR (whiteLabelId, method)
 * — é ele que delimita o otimismo em grupo — e o nome exibível do processador
 * para o toast de sucesso.
 */
export interface SetDefaultProcessorConfigVariables {
  uuid: string
  whiteLabelId: string
  method: ProcessorPaymentMethod
  processorName: string
}

// Par (alvo + WL + método) que delimita o patch atômico do padrão no cache —
// o subconjunto das variables da eleição que o patch de fato usa.
type DefaultForMethodPatchTarget = Pick<
  SetDefaultProcessorConfigVariables,
  "uuid" | "whiteLabelId" | "method"
>

/**
 * Patch imutável do cache da lista espelhando a troca ATÔMICA do backend:
 * liga o alvo e desliga qualquer outro padrão do MESMO par. Caches que não
 * são a lista (detail, events — mesma chave base) passam intocados.
 */
export const patchDefaultForMethod = (
  cache: ProcessorConfigListResponse | undefined,
  variables: DefaultForMethodPatchTarget
): ProcessorConfigListResponse | undefined => {
  if (!cache || !Array.isArray(cache.content)) return cache
  return {
    ...cache,
    content: cache.content.map(
      (item): ProcessorConfigItem => {
        if (item.id === variables.uuid) {
          return { ...item, defaultForMethod: true }
        }
        const isSamePair =
          item.whiteLabelId === variables.whiteLabelId &&
          item.method === variables.method
        return isSamePair &&
          (item.defaultForMethod ?? false)
          ? { ...item, defaultForMethod: false }
          : item
      }
    ),
  }
}

/**
 * Espelha no cache o `defaultForMethod` que o activate/deactivate devolve:
 * `true` reusa o patch atômico do par (marca o alvo e desmarca os irmãos —
 * mesmo desenho do set-default); `false` seta só o campo do próprio item.
 * O par (WL+método) sai do item já em cache — o response do toggle não o traz.
 */
export const patchDefaultForMethodFromToggle = (
  cache: ProcessorConfigListResponse | undefined,
  uuid: string,
  defaultForMethod: boolean
): ProcessorConfigListResponse | undefined => {
  if (!cache || !Array.isArray(cache.content)) return cache
  const target = cache.content.find(
    (item) => item.id === uuid
  )
  if (!target) return cache
  if (!defaultForMethod) {
    return {
      ...cache,
      content: cache.content.map(
        (item): ProcessorConfigItem =>
          item.id === uuid
            ? { ...item, defaultForMethod: false }
            : item
      ),
    }
  }
  return patchDefaultForMethod(cache, {
    uuid,
    whiteLabelId: target.whiteLabelId,
    method: target.method,
  })
}

// Elege a config como padrão do par (POST /{uuid}/set-default).
export const setDefaultProcessorConfig = async (
  uuid: string
): Promise<ProcessorConfigToggleResponse> => {
  const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_SET_DEFAULT.replace("{{uuid}}", uuid)}`
  const { data } =
    await api.post<ProcessorConfigToggleResponse>(url)
  return data
}

/**
 * Eleição do processador padrão — otimista EM GRUPO (molde do toggle
 * activate/deactivate): marca o alvo e desmarca o irmão do mesmo par em TODAS
 * as queries da feature, rollback duplo via snapshot no erro e invalidação
 * por predicate no settled. Os 409 do backend (config inativa, eleição
 * concorrente) viram mensagens i18n acionáveis.
 */
export const useSetDefaultProcessorConfigMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    ProcessorConfigToggleResponse,
    unknown,
    SetDefaultProcessorConfigVariables,
    ToggleSnapshotContext
  >({
    mutationFn: async (variables) =>
      setDefaultProcessorConfig(variables.uuid),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
      })
      const previous = queryClient.getQueriesData<
        ProcessorConfigListResponse | undefined
      >({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
      })
      queryClient.setQueriesData<
        ProcessorConfigListResponse | undefined
      >(
        {
          predicate: (query) =>
            query.queryKey[0] ===
            ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
        },
        (cache) => patchDefaultForMethod(cache, variables)
      )
      return { previous }
    },
    onSuccess: (_result, variables) => {
      toast({
        title: i18n.t(
          "pages.adminProcessorConfig.byMethod.setDefault.successToast",
          { processor: variables.processorName }
        ),
        variant: "success",
      })
    },
    onError: (error, _variables, context) => {
      // Rollback DUPLO: os snapshots restauram o alvo E o irmão desmarcado.
      context?.previous.forEach(([queryKey, cache]) => {
        queryClient.setQueryData(queryKey, cache)
      })
      toast({
        title:
          defaultConflictMessage(error) ??
          getApiErrorMessage(
            error,
            i18n.t(
              "pages.adminProcessorConfig.byMethod.errors.setDefaultError"
            )
          ),
        variant: "destructive",
      })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
      })
    },
  })
}

// ── Detalhe + edição (wave 2 — Sheet) ───────────────────────────────

// Detalhe da configuração (GET /{uuid}) — provider mascarado.
export const getProcessorConfigDetail = async (
  uuid: string
): Promise<ProcessorConfigDetail> => {
  const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_DETAIL.replace("{{uuid}}", uuid)}`
  const { data } = await api.get<ProcessorConfigDetail>(url)
  return data
}

// Opções da query de detalhe — `gcTime: 0` (dado de credencial
// nunca renderiza de cache velho ao reabrir o Sheet).
export const processorConfigDetailQueryOptions = (
  uuid: string
) => ({
  queryKey: [
    ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
    "detail",
    uuid,
  ] as const,
  queryFn: async () => getProcessorConfigDetail(uuid),
  enabled: Boolean(uuid),
  refetchOnMount: "always" as const,
  gcTime: 0,
  placeholderData: undefined,
})

export const useProcessorConfigDetail = (uuid: string) =>
  useQuery(processorConfigDetailQueryOptions(uuid))

// PATCH /{uuid} — update PARCIAL do provider (só o que mudou;
// write-only não alterado FICA FORA; mascarado reenviado = 400).
export const useUpdateProcessorConfigMutation = (
  uuid: string
) => {
  const queryClient = useQueryClient()

  return useMutation<
    ProcessorConfigDetail,
    unknown,
    ProcessorConfigUpdatePayload
  >({
    mutationFn: async (payload) => {
      const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_DETAIL.replace("{{uuid}}", uuid)}`
      const { data } =
        await api.patch<ProcessorConfigDetail>(url, payload)
      return data
    },
    onSuccess: async () => {
      toast({
        title: "Configuração atualizada",
        variant: "success",
      })
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
      })
    },
    onError: (error) => {
      toast({
        title: getApiErrorMessage(
          error,
          "Erro ao atualizar a configuração"
        ),
        variant: "destructive",
      })
    },
  })
}

// POST /{uuid}/test-connection — testa a conexão real com o
// processador (200 mesmo em falha: success=false + detail).
export const useTestProcessorConnectionMutation = (
  uuid: string
) =>
  useMutation<ProcessorTestConnectionResponse, unknown>({
    mutationFn: async () => {
      const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_TEST_CONNECTION.replace("{{uuid}}", uuid)}`
      const { data } =
        await api.post<ProcessorTestConnectionResponse>(url)
      return data
    },
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: `Conexão OK (${result.latencyMs}ms)`,
          variant: "success",
        })
        return
      }
      toast({
        title: result.detail
          ? `Falha na conexão: ${result.detail}`
          : "Falha na conexão com o processador",
        variant: "destructive",
      })
    },
    onError: (error) => {
      toast({
        title: getApiErrorMessage(
          error,
          "Erro ao testar a conexão"
        ),
        variant: "destructive",
      })
    },
  })

// ── Criação (wave 2 — stepper §6.3) ─────────────────────────────────

// Métodos que o white label PODE usar (payment agrega o backoffice).
// A API está sendo implementada em paralelo — o response é
// normalizado (array puro OU { methods: [] }) e o stepper trata erro
// com FALLBACK gracioso (só a matriz do metadata + aviso discreto).
export const getAllowedMethods = async (
  whiteLabelUuid: string
): Promise<ProcessorPaymentMethod[]> => {
  const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_ALLOWED_METHODS.replace("{{uuid}}", whiteLabelUuid)}`
  const { data } = await api.get<
    | ProcessorPaymentMethod[]
    | { methods?: ProcessorPaymentMethod[] }
  >(url)
  if (Array.isArray(data)) return data
  return data?.methods ?? []
}

// Opções da query de allowed-methods (exportadas p/ teste).
export const allowedMethodsQueryOptions = (
  whiteLabelUuid: string
) => ({
  queryKey: [
    ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
    "allowed-methods",
    whiteLabelUuid,
  ] as const,
  queryFn: async () => getAllowedMethods(whiteLabelUuid),
  enabled: Boolean(whiteLabelUuid),
  // Erro NÃO re-tenta em loop no stepper — o fallback assume.
  retry: false,
  gcTime: 0,
  placeholderData: undefined,
})

export const useAllowedMethods = (whiteLabelUuid: string) =>
  useQuery(allowedMethodsQueryOptions(whiteLabelUuid))

/**
 * Entradas do cofre que as configurações deste white label podem apontar.
 *
 * Difere de `useMerchantMtlsCertificates`, que lista o cofre do usuário logado: o cofre é do
 * merchant, um superAdmin administra white labels de vários, e o vínculo é validado contra o
 * merchant da configuração. Listar o cofre errado faz o operador escolher um certificado que o
 * save recusa com 404.
 */
export const vaultCertificatesQueryOptions = (
  whiteLabelUuid: string
) => ({
  queryKey: [
    ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
    "vault-certificates",
    whiteLabelUuid,
  ] as const,
  queryFn: async (): Promise<MerchantMtlsCertificate[]> => {
    const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_VAULT_CERTIFICATES.replace("{{uuid}}", whiteLabelUuid)}`
    const { data } =
      await api.get<MerchantMtlsCertificate[]>(url)
    return data
  },
  enabled: Boolean(whiteLabelUuid),
  // Material de segurança: cache velho mostraria um certificado já substituído.
  gcTime: 0,
})

export const useVaultCertificates = (
  whiteLabelUuid: string
) => useQuery(vaultCertificatesQueryOptions(whiteLabelUuid))

// POST / — cria config + provider. 409 = o par WL+método já tem
// configuração ATIVA (toast com orientação clara).
export const useCreateProcessorConfigMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    ProcessorConfigDetail,
    unknown,
    ProcessorConfigCreatePayload
  >({
    mutationFn: async (payload) => {
      const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG}`
      const { data } =
        await api.post<ProcessorConfigDetail>(url, payload)
      return data
    },
    onSuccess: async () => {
      toast({
        title: "Configuração criada",
        variant: "success",
      })
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
      })
    },
    onError: (error) => {
      toast({
        title: hasStatusCode(error, 409)
          ? "Já existe configuração ATIVA deste white label + método — desative-a antes de criar outra."
          : getApiErrorMessage(
              error,
              "Erro ao criar a configuração"
            ),
        variant: "destructive",
      })
    },
  })
}

// ── Certificado (wave 3 — fases 4/5 do payment) ─────────────────────

// Histórico de eventos de certificado (GET .../certificate/history,
// Page<> do Spring, mais recente primeiro).
export const getCertificateHistory = async (
  uuid: string,
  page: number,
  quantity: number
): Promise<CertificateHistoryResponse> => {
  const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_CERTIFICATE_HISTORY.replace("{{uuid}}", uuid)}?page=${page}&quantity=${quantity}`
  const { data } =
    await api.get<CertificateHistoryResponse>(url)
  return data
}

export const certificateHistoryQueryOptions = (
  uuid: string,
  page: number,
  quantity: number
) => ({
  queryKey: [
    ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
    "certificate-history",
    uuid,
    page,
    quantity,
  ] as const,
  queryFn: async () =>
    getCertificateHistory(uuid, page, quantity),
  enabled: Boolean(uuid),
  gcTime: 0,
  placeholderData: undefined,
})

export const useCertificateHistory = (
  uuid: string,
  page: number,
  quantity: number
) =>
  useQuery(
    certificateHistoryQueryOptions(uuid, page, quantity)
  )

/**
 * Apaga a configuração — o vínculo entre o meio de pagamento e o processador.
 *
 * O backend só apaga o que está INATIVO (409 na ativa): configuração ativa é o que faz o
 * pagamento rotear, e removê-la direto derrubaria o meio de pagamento sem etapa intermediária.
 */
export const useDeleteProcessorConfigMutation = (
  onSuccess?: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (configId: string) => {
      await api.delete(
        `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_DETAIL.replace("{{uuid}}", configId)}`
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
            ADMIN_PROCESSOR_CONFIG_QUERY_KEY ||
          query.queryKey[0] === "admin-merchant-processors",
      })
      toast({
        title: "Meio de pagamento removido.",
        variant: "success",
      })
      onSuccess?.()
    },
    onError: (error: unknown) => {
      toast({
        variant: "destructive",
        title: getApiErrorMessage(
          error,
          "Não foi possível remover o meio de pagamento."
        ),
      })
    },
  })
}

/**
 * Trilha do meio de pagamento: quem ligou, desligou, moveu de credencial ou apagou.
 *
 * Página 0-based, como todo `Page<>` do Spring. `gcTime: 0` porque a trilha é o
 * registro de decisões com consequência financeira — mostrar uma versão de cache
 * depois de uma troca seria esconder a própria mudança que acabou de acontecer.
 */
export const configEventsQueryOptions = (
  uuid: string,
  page = 0,
  quantity = 20
) => ({
  queryKey: [
    ADMIN_PROCESSOR_CONFIG_QUERY_KEY,
    "events",
    uuid,
    page,
    quantity,
  ] as const,
  queryFn:
    async (): Promise<ConfigurationEventsResponse> => {
      const url = `${BASE_URL}${ENDPOINTS.ADMIN_PROCESSOR_CONFIG_EVENTS.replace("{{uuid}}", uuid)}?page=${page}&quantity=${quantity}`
      const { data } =
        await api.get<ConfigurationEventsResponse>(url)
      return data
    },
  enabled: Boolean(uuid),
  gcTime: 0,
  // A trilha mostra o erro na própria seção; sem isto o operador levaria também
  // um toast de alarme por uma seção declaradamente acessória.
  meta: { skipErrorNotification: true },
})

export const useConfigEvents = (
  uuid: string,
  page = 0,
  quantity = 20
) =>
  useQuery(configEventsQueryOptions(uuid, page, quantity))
